<script setup lang="ts">
import { useWordProcessorStore } from "./useWordProcessorStore";

defineProps({
  toolbarId: {
    type: String,
    required: true,
  },
});

const editorRef = inject("quillEditor") as any;
const wordProcessorInstanceId = inject("instanceId") as string;
const wordProcessorStore = useWordProcessorStore(wordProcessorInstanceId);
const { quillDelta, isEditorReady } = storeToRefs(wordProcessorStore);

const canUndo = ref(false);
const canRedo = ref(false);

const syncHistoryState = () => {
  const history = editorRef.value?.getQuill?.()?.history;
  canUndo.value = (history?.stack?.undo?.length ?? 0) > 0;
  canRedo.value = (history?.stack?.redo?.length ?? 0) > 0;
};

watch(
  [quillDelta, isEditorReady],
  async () => {
    await nextTick();
    syncHistoryState();
  },
  { immediate: true, flush: "post" }
);

const undo = () => {
  if (!canUndo.value) return;
  editorRef.value?.getQuill?.()?.history?.undo();
  nextTick(syncHistoryState);
};

const redo = () => {
  if (!canRedo.value) return;
  editorRef.value?.getQuill?.()?.history?.redo();
  nextTick(syncHistoryState);
};
</script>

<template>
  <div
    :id="toolbarId"
    class="@container border shadow rounded flex flex-row justify-between p-2 document-editor-toolbar mb-2"
  >
    <div class="flex flex-row items-center gap-1 grow flex-wrap pl-1">
      <div class="flex items-center gap-1 shrink-0">
        <BaseWordProcessorToolbarTextSize />
      </div>

      <div class="h-5 w-px bg-medium-tint/30 shrink-0" />

      <div class="flex items-center gap-1 shrink-0">
        <BaseWordProcessorToolbarFormatting
          formatting-attribute="bold"
          tooltip="Bold"
        >
          <icon name="lucide:bold" size="16" />
        </BaseWordProcessorToolbarFormatting>
        <BaseWordProcessorToolbarFormatting
          formatting-attribute="italic"
          tooltip="Italic"
        >
          <icon name="lucide:italic" size="16" />
        </BaseWordProcessorToolbarFormatting>
        <BaseWordProcessorToolbarFormatting
          formatting-attribute="underline"
          tooltip="Underline"
        >
          <icon name="lucide:underline" size="16" />
        </BaseWordProcessorToolbarFormatting>
        <BaseWordProcessorToolbarFormatting
          formatting-attribute="strike"
          tooltip="Strikethrough"
        >
          <icon name="lucide:strikethrough" size="16" />
        </BaseWordProcessorToolbarFormatting>
        <BaseWordProcessorToolbarTextColor />
        <BaseWordProcessorToolbarHighlightColor />
        <BaseWordProcessorToolbarShowFormatting />
      </div>

      <div class="h-5 w-px bg-medium-tint/30 mx-1 shrink-0" />

      <div
        class="flex basis-full @[700px]:basis-auto items-center gap-1 shrink-0 order-2 @[700px]:order-none"
      >
        <div class="flex items-center gap-1 shrink-0">
          <BaseWordProcessorToolbarAlignment />
          <div class="hidden @[700px]:flex items-center gap-1 shrink-0">
            <button
              v-tooltip="'Decrease Indent'"
              aria-label="Decrease indent"
              class="ql-indent rounded !w-[30px] !h-[30px] !p-[6px]"
              value="-1"
            />
            <button
              v-tooltip="'Increase Indent'"
              aria-label="Increase indent"
              class="ql-indent rounded !w-[30px] !h-[30px] !p-[6px]"
              value="+1"
            />
            <BaseWordProcessorToolbarHangingIndent />
          </div>
          <div class="@[700px]:hidden">
            <BaseWordProcessorToolbarIndentationMenu />
          </div>
          <BaseWordProcessorToolbarLineSpacing />
        </div>

        <div class="h-5 w-px bg-medium-tint/30 mx-1 shrink-0" />

        <div class="flex items-center gap-1 shrink-0">
          <BaseWordProcessorToolbarDictation />
          <BaseWordProcessorToolbarPageBreak />
        </div>
      </div>

      <div
        class="flex items-center gap-1 shrink-0 ml-auto order-1 @[700px]:order-none"
      >
        <slot name="actions" />
        <BaseIconButton
          tooltip="Undo"
          aria-label="Undo"
          :aria-disabled="!canUndo"
          :disabled="!canUndo"
          @mousedown.prevent.stop="undo"
        >
          <icon name="lucide:undo-2" size="16" />
        </BaseIconButton>
        <BaseIconButton
          tooltip="Redo"
          aria-label="Redo"
          :aria-disabled="!canRedo"
          :disabled="!canRedo"
          @mousedown.prevent.stop="redo"
        >
          <icon name="lucide:redo-2" size="16" />
        </BaseIconButton>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
